import { ArrowLeft, Clock3, KeyRound, ShieldCheck } from "lucide-react";
import { useEffect, useId, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaOtpInput } from "@/shared/components/forms/ColocaOtpInput";
import { ColocaDialog, ColocaDialogClose } from "@/shared/components/overlays/ColocaDialog";
import {
  EmailCodeFace,
  EmailCodeFooter,
  EmailCodeOffer,
  EmailCodeTitle,
} from "@/shared/components/security/EmailCodeChallenge";
import {
  SecurityPinInput,
  SecurityPinSetupActions,
  SecurityPinSetupBody,
  SecurityPinStepper,
} from "@/shared/components/security/SecurityPinSetup";
import {
  type EmailCodeChallenge,
  useEmailCodeChallenge,
} from "@/shared/components/security/useEmailCodeChallenge";
import { useSecurityPinSetup } from "@/shared/components/security/useSecurityPinSetup";
import { useNow } from "@/shared/hooks/useNow";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { readHumanErrorMessage } from "@/shared/utils/apiError";
import { formatLockRemaining, stepUpLockOf, stepUpMethodOf } from "@/shared/utils/stepUp";

export interface TwoFactorChallengeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Runs the guarded request with the typed code. Throw to keep the modal open and show the error (a wrong
   * code); resolve to close it. Any other failure belongs to the caller: close the modal yourself and handle
   * it where the action lives.
   */
  onSubmit: (code: string) => Promise<void>;
  title?: string;
  description?: string;
  /**
   * What the prompt says to someone who confirms with their security PIN. `description` often names the
   * authenticator app, so it is not shown to them; pass the action's context here when it has some.
   */
  pinDescription?: string;
}

/**
 * Step-up prompt for a request that carries `x-two-factor-code`: six digits, verify, cancel. It asks for what
 * the signed-in user confirms with — the authenticator code, or the security PIN (masked, with its lock and a
 * way to set a new one when it is forgotten or locked). Pre-check the method with `hasStepUpMethod` before
 * opening it — a user without one gets `TwoFactorRequiredModal` instead, because the backend would answer 403
 * `TWO_FACTOR_REQUIRED` and the layout would redirect them to Settings mid-flow.
 *
 * With the email backup on (`step_up_email_enabled`), both faces offer "get a code by email": the same
 * `onSubmit` runs with `email`, the server's 401 `STEP_UP_EMAIL_SENT` turns the dialog into "check your
 * email", and the code typed there goes out as `email:NNNNNN`. `onSubmit` must therefore throw the refusal
 * with its server code (`stepUpRefusal`) for the challenge to tell "sent" from "wrong".
 *
 * @summary 6-digit challenge dialog (authenticator code, security PIN or email code) for step-up actions
 */
export function TwoFactorChallengeModal(props: TwoFactorChallengeModalProps) {
  const { user } = useAuth();
  const email = useEmailCodeChallenge(props.open, props.onOpenChange, props.onSubmit);
  if (stepUpMethodOf(user) === "pin") return <PinChallenge {...props} email={email} />;
  return <AuthenticatorChallenge {...props} email={email} />;
}

interface ChallengeFaceProps extends TwoFactorChallengeModalProps {
  email: EmailCodeChallenge;
}

/** The submit loop both faces share: one request at a time, the thrown message shown, the field cleared. */
function useChallengeSubmit(
  open: boolean,
  onOpenChange: (open: boolean) => void,
  onSubmit: (code: string) => Promise<void>,
  fallback: string,
) {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [refusals, setRefusals] = useState(0);

  useEffect(() => {
    if (!open) {
      setCode("");
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const submit = async (value: string) => {
    if (value.length !== 6 || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(value);
      onOpenChange(false);
    } catch (err: unknown) {
      // Never `err.message` on its own: an AxiosError IS an Error, and its message is the literal
      // "Request failed with status code N". `readHumanErrorMessage` is the shared guarantee against that;
      // a caller throwing a deliberate, translated `Error` (the documented way to report a wrong code) still
      // renders exactly as before.
      setError(readHumanErrorMessage(err, fallback));
      setRefusals((n) => n + 1);
      setCode("");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setCode("");
    setError(null);
  };

  return { code, setCode, error, submitting, refusals, submit, reset };
}

function AuthenticatorChallenge({
  open,
  onOpenChange,
  onSubmit,
  title,
  description,
  email,
}: ChallengeFaceProps) {
  const { t } = useTranslations("global");
  const { user } = useAuth();
  const formId = useId();
  const { code, setCode, error, submitting, refusals, submit } = useChallengeSubmit(
    open,
    onOpenChange,
    onSubmit,
    t("two_factor_challenge_error_invalid_code"),
  );

  // Five wrong codes in a row lock the authenticator for 15 minutes too: the field waits out the countdown
  // instead of spending requests the server would refuse anyway.
  const now = useNow(open && !!user?.step_up_locked_until);
  const lock = stepUpLockOf(user, now);
  const locked = lock?.kind === "temporary";
  const busy = submitting || email.busy;

  return (
    <ColocaDialog
      open={open}
      // While the code is being verified the request is out: Escape, the backdrop and the × must not close the
      // dialog and report a cancel for an operation that may still go through.
      onOpenChange={(next) => {
        if (!next && busy) return;
        onOpenChange(next);
      }}
      showCloseButton={!busy}
      size="sm"
      title={
        email.active ? (
          <EmailCodeTitle />
        ) : (
          <span className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" aria-hidden="true" />
            {title ?? t("two_factor_challenge_title")}
          </span>
        )
      }
      description={email.active ? email.message : (description ?? t("two_factor_challenge_description"))}
      footer={
        email.active ? (
          <EmailCodeFooter email={email} formId={formId} />
        ) : (
          <>
            <ColocaDialogClose render={<ColocaButton variant="ghost" disabled={busy} />}>
              {t("two_factor_required_modal_cancel")}
            </ColocaDialogClose>
            <ColocaButton
              variant="primary"
              type="submit"
              form={formId}
              loading={submitting}
              disabled={code.length !== 6 || busy || locked}
            >
              {t("two_factor_challenge_submit")}
            </ColocaButton>
          </>
        )
      }
    >
      {email.active ? (
        <EmailCodeFace email={email} method="totp" formId={formId} />
      ) : (
        <form
          id={formId}
          onSubmit={(event) => {
            event.preventDefault();
            void submit(code);
          }}
          className="flex flex-col items-center gap-3 pt-2"
        >
          {lock?.kind === "temporary" ? (
            <ColocaAlert
              variant="warning"
              icon={Clock3}
              heading={t("two_factor_challenge_totp_locked_title")}
              text={t("two_factor_challenge_totp_locked", {
                time: formatLockRemaining(lock.until.getTime() - now),
              })}
              className="w-full animate-fade-down animate-duration-300 motion-reduce:animate-none"
            />
          ) : null}
          {/* Keyed by the refusals so a wrong code shakes the row once more, like the PIN's. */}
          <div key={refusals} className={cn(refusals > 0 && "animate-shake motion-reduce:animate-none")}>
            <ColocaOtpInput
              autoFocus
              value={code}
              onValueChange={setCode}
              onComplete={(value) => void submit(value)}
              invalid={!!error}
              disabled={locked || busy}
              aria-label={t("two_factor_challenge_title")}
            />
          </div>
          {error && !locked ? (
            <p
              role="alert"
              className="animate-fade text-center text-sm text-destructive animate-duration-200 motion-reduce:animate-none"
            >
              {error}
            </p>
          ) : null}
          <EmailCodeOffer email={email} method="totp" emphasize={locked} />
        </form>
      )}
    </ColocaDialog>
  );
}

function PinChallenge({ open, onOpenChange, onSubmit, title, pinDescription, email }: ChallengeFaceProps) {
  const { t } = useTranslations("global");
  const { user } = useAuth();
  const formId = useId();
  const [phase, setPhase] = useState<"verify" | "reset">("verify");
  const { code, setCode, error, submitting, refusals, submit, reset } = useChallengeSubmit(
    open,
    onOpenChange,
    onSubmit,
    t("two_factor_challenge_error_invalid_code"),
  );

  const lockedUntil = user?.step_up_locked_until ?? null;
  const now = useNow(open && !!lockedUntil);
  const lock = stepUpLockOf(user, now);

  // Closing the prompt drops a half-done PIN reset, so the next opening asks for the PIN again.
  const [wasOpen, setWasOpen] = useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    if (!open) setPhase("verify");
  }

  if (phase === "reset") {
    return (
      <PinResetPhase
        onOpenChange={onOpenChange}
        onBack={() => setPhase("verify")}
        onDone={() => {
          reset();
          setPhase("verify");
        }}
      />
    );
  }

  const blocked = lock !== null;
  const busy = submitting || email.busy;
  const lockAlert =
    lock?.kind === "temporary" ? (
      <ColocaAlert
        variant="warning"
        icon={Clock3}
        heading={t("two_factor_challenge_pin_locked_title")}
        text={t("two_factor_challenge_pin_locked", { time: formatLockRemaining(lock.until.getTime() - now) })}
        className="animate-fade-down animate-duration-300 motion-reduce:animate-none"
      />
    ) : lock?.kind === "reset" ? (
      <ColocaAlert
        variant="destructive"
        icon={KeyRound}
        heading={t("two_factor_challenge_pin_locked_title")}
        text={t("two_factor_challenge_pin_reset_required")}
        className="animate-fade-down animate-duration-300 motion-reduce:animate-none"
      />
    ) : null;

  return (
    <ColocaDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && busy) return;
        onOpenChange(next);
      }}
      showCloseButton={!busy}
      size="sm"
      title={
        email.active ? (
          <EmailCodeTitle />
        ) : (
          <span className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-primary" aria-hidden="true" />
            {title ?? t("two_factor_challenge_pin_title")}
          </span>
        )
      }
      description={
        email.active ? email.message : (pinDescription ?? t("two_factor_challenge_pin_description"))
      }
      footer={
        email.active ? (
          <EmailCodeFooter email={email} formId={formId} />
        ) : (
          <>
            <ColocaDialogClose render={<ColocaButton variant="ghost" disabled={busy} />}>
              {t("two_factor_required_modal_cancel")}
            </ColocaDialogClose>
            {lock?.kind === "reset" ? (
              <ColocaButton variant="primary" icon={KeyRound} onClick={() => setPhase("reset")}>
                {t("two_factor_challenge_pin_new")}
              </ColocaButton>
            ) : (
              <ColocaButton
                variant="primary"
                type="submit"
                form={formId}
                loading={submitting}
                disabled={code.length !== 6 || busy || blocked}
              >
                {t("two_factor_challenge_submit")}
              </ColocaButton>
            )}
          </>
        )
      }
    >
      {email.active ? (
        <EmailCodeFace email={email} method="pin" formId={formId} />
      ) : (
        <form
          id={formId}
          onSubmit={(event) => {
            event.preventDefault();
            void submit(code);
          }}
          className="flex flex-col gap-3 pt-2"
        >
          {lockAlert}
          <SecurityPinInput
            value={code}
            onChange={setCode}
            onComplete={(value) => void submit(value)}
            invalid={!!error}
            shakeKey={refusals}
            label={t("two_factor_challenge_pin_title")}
            autoFocus
            disabled={blocked || busy}
          />
          {error && !blocked ? (
            <p
              role="alert"
              className="animate-fade text-center text-sm text-destructive animate-duration-200 motion-reduce:animate-none"
            >
              {error}
            </p>
          ) : null}
          {lock?.kind !== "reset" ? (
            <button
              type="button"
              onClick={() => setPhase("reset")}
              disabled={busy}
              className={cn(
                "self-center text-[12.5px] font-medium text-primary underline-offset-4 hover:underline disabled:pointer-events-none disabled:opacity-50",
                // While locked, the one that lets the action go on now pulses: the email code when it is on.
                blocked && !email.offered && "animate-pulse motion-reduce:animate-none",
              )}
            >
              {t("two_factor_challenge_pin_forgot")}
            </button>
          ) : null}
          {/* A PIN that needs replacing has closed every session: no code would help. */}
          {lock?.kind !== "reset" ? <EmailCodeOffer email={email} method="pin" emphasize={blocked} /> : null}
        </form>
      )}
    </ColocaDialog>
  );
}

/**
 * A forgotten or locked PIN is replaced right in the prompt, with the account password, and the person comes
 * back to the same challenge to type the new one — the action they were confirming is still waiting.
 */
function PinResetPhase({
  onOpenChange,
  onBack,
  onDone,
}: {
  onOpenChange: (open: boolean) => void;
  onBack: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslations("global");
  const pin = useSecurityPinSetup();
  const done = pin.step === "done";

  return (
    <ColocaDialog
      open
      onOpenChange={(next) => {
        if (!next && pin.saving) return;
        if (!next && done) return onDone();
        onOpenChange(next);
      }}
      showCloseButton={!pin.saving}
      size="sm"
      title={
        <span className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-primary" aria-hidden="true" />
          {t("security_pin_dialog_reset_title")}
        </span>
      }
      description={done ? undefined : t("security_pin_dialog_description")}
      toolbar={done ? undefined : <SecurityPinStepper step={pin.step} />}
      footer={
        <SecurityPinSetupActions
          controller={pin}
          submitLabel={t("security_pin_submit_change")}
          done={{ label: t("security_pin_done_back_to_challenge"), onClick: onDone }}
          leading={
            <ColocaButton variant="ghost" icon={ArrowLeft} onClick={onBack}>
              {t("security_pin_back")}
            </ColocaButton>
          }
        />
      }
    >
      <SecurityPinSetupBody controller={pin} doneBody={t("security_pin_done_body_challenge")} />
    </ColocaDialog>
  );
}
